import React, { createContext, MouseEvent, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import AppRedirect from './AppRedirect';
import { configuredStudentApps, StudentApp } from '../utils/studentApps';
import { useAppRedirect } from '../utils/useAppRedirect';
import { useMediaQuery } from '../utils/useMediaQuery';

/**
 * The student's app switcher, as the Öğrenci Paneli has it (its `AppSwitcher.tsx`): the apps' marks
 * as compact icons with tooltips. Pressing one runs the redirect screen (AppRedirect.tsx, timed by
 * utils/useAppRedirect.ts) and then goes to that app's SSO door in the same tab. Each icon is a real
 * link to the door, so that a modified click, reduced motion or a script that did not load still end
 * up there.
 *
 * Where the icons sit depends on the window, and only ONE copy is ever drawn (decided here in script,
 * not hidden in CSS):
 *  - at least `WIDE_QUERY` wide: in the top bar's row of icon buttons (TopBar.tsx);
 *  - narrower: in the strip above the top bar, which on a phone is the row the menu button is in.
 * The top bar's row has no room below that: its search field is ~325px at 1366px and the two icons
 * cost another ~130px, which would clip its placeholder (and at 1100px leave it ~50px).
 *
 * Renders nothing when the build names no app, and an app it does not name is not drawn: there is no
 * icon without somewhere to go (utils/studentApps.ts).
 */

/**
 * Wide enough for the icons to sit in the top bar and still leave the search field its whole
 * placeholder (about 250px): at 1440px the field is ~265px with the icons there, ~400px without them.
 */
export const WIDE_QUERY = '(min-width: 1440px)';

interface StudentAppsValue {
  apps: StudentApp[];
  open: (app: StudentApp, event: MouseEvent<HTMLElement>) => void;
  wide: boolean;
}

const StudentAppsContext = createContext<StudentAppsValue | null>(null);

/** The switcher's state, or null where there is none (a teacher, a parent, a page rendered alone). */
export function useStudentApps(): StudentAppsValue | null {
  return useContext(StudentAppsContext);
}

/** What the screen says when the app never answered (the panel's own words for it). */
export const gaveUpMessage = (app: StudentApp) => `${app.name} şu an açılamadı. Biraz sonra tekrar deneyebilirsin.`;
const NOTICE_MS = 8000;

function Notice({ message, onClose }: { message: string; onClose: () => void }) {
  useEffect(() => {
    const timer = setTimeout(onClose, NOTICE_MS);
    return () => clearTimeout(timer);
  }, [message, onClose]);
  return (
    <div role="alert" className="fixed bottom-4 left-4 right-4 z-[100] mx-auto max-w-xl rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950 shadow-lg">
      <div className="flex items-start gap-4">
        <p className="flex-1">{message}</p>
        <button type="button" aria-label="Uyarıyı kapat" onClick={onClose}>Kapat</button>
      </div>
    </div>
  );
}

/**
 * Owns the switcher for one student session: the apps, the redirect screen and what it needs of the
 * page. While the screen is up the page behind it is `inert` (nothing in it takes focus or a click),
 * and the screen sits OUTSIDE that subtree so that it is not.
 */
export const StudentAppsHost: React.FC<{ children: ReactNode; go?: (href: string) => void }> = ({ children, go }) => {
  const apps = useMemo(configuredStudentApps, []);
  const [notice, setNotice] = useState<string | null>(null);
  const closeNotice = useCallback(() => setNotice(null), []);
  const { active, open, cancel } = useAppRedirect(go, (app) => setNotice(gaveUpMessage(app)));
  const wide = useMediaQuery(WIDE_QUERY);
  const value = useMemo(() => ({ apps, open, wide }), [apps, open, wide]);
  return (
    <StudentAppsContext.Provider value={value}>
      <div inert={active !== null}>{children}</div>
      {active && <AppRedirect redirect={active} onCancel={cancel} />}
      {notice && <Notice message={notice} onClose={closeNotice} />}
    </StudentAppsContext.Provider>
  );
};

/**
 * The icons. `site` says which of the two places this is: it draws only where the window calls for
 * it, so the top bar and the strip can both hold one without ever showing two.
 */
export const StudentAppIcons: React.FC<{ site: 'header' | 'strip' }> = ({ site }) => {
  const switcher = useStudentApps();
  if (!switcher || switcher.apps.length === 0 || switcher.wide !== (site === 'header')) return null;
  return (
    <nav
      aria-label="Uygulamalar"
      title="Uygulamalar"
      data-site={site}
      className="flex flex-shrink-0 items-center gap-1 rounded-[24px] bg-digipo-card px-2 py-1.5 shadow-header-card"
    >
      {switcher.apps.map((app) => (
        <a
          key={app.key}
          href={app.href}
          title={app.name}
          aria-label={`${app.name} uygulamasına geç`}
          onClick={(event) => switcher.open(app, event)}
          className="grid h-11 w-11 place-items-center rounded-[18px] transition-[background-color,box-shadow] duration-200 hover:bg-white hover:shadow-header-card focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-digipo-blue"
        >
          <img src={app.mark} alt="" width={28} height={28} className="h-7 w-7 object-contain" />
        </a>
      ))}
    </nav>
  );
};
